// Light or dark, chosen once and remembered.
//
// Until the author chooses, the device decides and keeps deciding: a system
// that switches at dusk switches the app with it. The moment they press the
// toggle their choice is stored and the system is no longer consulted.

import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark";

const KEY = "metis.theme";
const listeners = new Set<() => void>();

let chosen: Theme | null = read();
let current: Theme = chosen ?? system();

function read(): Theme | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === "light" || raw === "dark" ? raw : null;
  } catch {
    return null;
  }
}

function system(): Theme {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

function set(theme: Theme): void {
  current = theme;
  document.documentElement.dataset.theme = theme;
  for (const listener of listeners) listener();
}

/** Called once before the first render, so the page never flashes the wrong
 *  colours on the way in. */
export function initTheme(): void {
  set(current);
  try {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (e) => {
        if (chosen === null) set(e.matches ? "dark" : "light");
      });
  } catch {
    // An older engine without matchMedia listeners keeps what it started with.
  }
}

export function toggleTheme(): void {
  chosen = current === "dark" ? "light" : "dark";
  try {
    localStorage.setItem(KEY, chosen);
  } catch {
    // Storage can be unavailable; the choice then lasts for this visit.
  }
  set(chosen);
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, () => current, () => current);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
